// =============================================================================
// FILE: packages/renderer/src/components/public/ProjectConcierge.tsx
// PURPOSE: "Project Management Concierge" landing-page lead magnet. A guest
//   intake wizard that turns a described project into an instant, branded
//   Project Startup Package PDF — reusing the multi-trade Strategy engine, the
//   Labor & Manning forecast, and the Standards compliance pass. Ends with a
//   100-day trial invitation. Optional $149 consultation checkout (graceful).
// =============================================================================

import { useState, type JSX } from 'react';
import { X, Sparkles, Loader2, Download, ArrowRight, Rocket, MapPin } from 'lucide-react';
import { analyzeMultiTrade, type TradeKey } from '../../lib/contextAnalyzer.js';
import { buildLabor } from '../../lib/laborAgent.js';
import { runAudit, type Region, REGIONS } from '../../lib/standardsAuditor.js';
import { generateMasterProposal, type ProposalResult } from '../../lib/masterProposal.js';
import type { DashboardStats } from '../../hooks/useDashboard.js';

const backendUrl = (import.meta.env.VITE_BACKEND_URL as string) ?? '';
const CONSULT_FEE = 149;

const TRADES: Array<{ key: TradeKey; label: string; kw: string }> = [
  { key: 'telecom',      label: 'Low-Voltage / Telecom', kw: 'low-voltage telecom Category 6A cabling' },
  { key: 'fire_alarm',   label: 'Fire Alarm',            kw: 'fire alarm FACP smoke detectors NFPA 72 notification appliances' },
  { key: 'electrical',   label: 'Electrical / Power',    kw: 'electrical power panels NEC/CEC lighting fixtures' },
  { key: 'plumbing',     label: 'Plumbing',              kw: 'plumbing fixtures water closets restrooms' },
  { key: 'architecture', label: 'Architecture',          kw: 'architecture floor plan buildings walls' },
];

export function ProjectConcierge({ onClose }: { onClose: () => void }): JSX.Element {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState('New Campus Project');
  const [location, setLocation] = useState('Denver, CO');
  const [region, setRegion] = useState<Region>('US_NEC');
  const [buildings, setBuildings] = useState(2);
  const [floors, setFloors] = useState(4);
  const [workstations, setWorkstations] = useState(120);
  const [serverRooms, setServerRooms] = useState(3);
  const [selected, setSelected] = useState<Set<TradeKey>>(new Set(['telecom', 'fire_alarm', 'electrical']));
  const [busy, setBusy] = useState(false);
  const [pkg, setPkg] = useState<ProposalResult | null>(null);
  const [payMsg, setPayMsg] = useState('');

  const toggle = (k: TradeKey) => setSelected((s) => {
    const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n;
  });

  const synthText = (): string => {
    const kws = TRADES.filter((t) => selected.has(t.key)).map((t) => t.kw).join('. ');
    return `PROJECT: ${name} — ${location}. ${buildings} buildings, ${floors} floors. ` +
      `${workstations} workstations. ${serverRooms} server rooms. Category 6A required. ${kws}. Per ANSI/TIA-568, NEC/CEC, NFPA 72 & BICSI TDMM.`;
  };

  const synthStats = (): DashboardStats => ({
    connections: workstations, ports: workstations,
    racks: Math.max(serverRooms, 1),
    patchPanels: Math.ceil(workstations / 48),
    switches: Math.ceil(workstations / 44),
    assets: Math.round(workstations * 0.4),
    fiberCables: Math.max(0, serverRooms - 1),
  } as unknown as DashboardStats);

  const generate = async () => {
    setBusy(true); setStep(3); setPayMsg('Preparing your consultation…');
    try {
      const res = await fetch(`${backendUrl}/api/pricing/deposit-order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: `concierge-${Date.now()}`, amount: CONSULT_FEE.toFixed(2), currency: 'USD', description: 'RCD² Project Startup Package' }),
      });
      if (res.ok) {
        const d = await res.json() as { approval_url?: string };
        if (d.approval_url) window.open(d.approval_url, '_blank', 'noopener');
        setPayMsg('Checkout opened. Your Project Startup Package is ready below.');
      } else setPayMsg('Preview unlocked for this session.');
    } catch { setPayMsg('Preview unlocked for this session.'); }

    try {
      const trade = analyzeMultiTrade(synthText());
      const labor = buildLabor(synthStats(), 4);
      const audit = runAudit({ region, longestRunFt: 290, trayFillPct: 38, hasTmgb: true, firestopConfirmed: true, separationMm: 220 });
      const result = await generateMasterProposal({
        project: name, author: 'Prepared by RCD² Concierge', strategy: trade.strategy,
        trade, manning: labor.manning, audit, priceUsd: CONSULT_FEE,
      });
      setPkg(result);
    } catch (err) {
      setPayMsg(`Could not render package: ${err instanceof Error ? err.message : String(err)}`);
    } finally { setBusy(false); }
  };

  const download = () => { if (pkg) { const a = document.createElement('a'); a.href = pkg.blobUrl; a.download = pkg.fileName; a.click(); } };

  return (
    <div data-testid="project-concierge" className="fixed inset-0 z-[10000] grid place-items-center p-6" style={{ background: 'rgba(4,7,14,0.9)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-2xl rounded-2xl overflow-hidden" style={{ background: 'rgba(9,13,22,0.98)', border: '1px solid rgba(37,181,168,0.4)', boxShadow: '0 30px 120px rgba(0,0,0,0.75)', maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(37,181,168,0.2)' }}>
          <div className="flex items-center gap-3">
            <Rocket className="h-5 w-5 text-teal-300" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300/80">Project Management Concierge</div>
              <div className="text-sm font-bold text-zinc-100">{step === 3 ? 'Your Project Startup Package' : `Tell R² about your project (${step}/2)`}</div>
            </div>
          </div>
          <button data-testid="concierge-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}><X className="h-4 w-4" /></button>
        </div>

        <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(92vh - 66px)' }}>
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-[13px] text-zinc-400">R² will manage the first 20% of your project — strategy, manning forecast, compliance map and budget — instantly.</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="block col-span-2"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Project name</span>
                  <input data-testid="concierge-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[13px] text-zinc-100" /></label>
                <label className="block"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</span>
                  <input data-testid="concierge-location" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[13px] text-zinc-100" /></label>
                <label className="block"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Region / code</span>
                  <select data-testid="concierge-region" value={region} onChange={(e) => setRegion(e.target.value as Region)} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[12px] text-zinc-100">
                    {(Object.keys(REGIONS) as Region[]).map((r) => <option key={r} value={r}>{REGIONS[r].label}</option>)}
                  </select></label>
                <label className="block"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Buildings</span>
                  <input data-testid="concierge-buildings" type="number" min={1} value={buildings} onChange={(e) => setBuildings(Number(e.target.value))} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[13px] text-zinc-100" /></label>
                <label className="block"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Floors</span>
                  <input data-testid="concierge-floors" type="number" min={1} value={floors} onChange={(e) => setFloors(Number(e.target.value))} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[13px] text-zinc-100" /></label>
                <label className="block"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Workstations</span>
                  <input data-testid="concierge-workstations" type="number" min={1} value={workstations} onChange={(e) => setWorkstations(Number(e.target.value))} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[13px] text-zinc-100" /></label>
                <label className="block"><span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Server rooms</span>
                  <input data-testid="concierge-serverrooms" type="number" min={1} value={serverRooms} onChange={(e) => setServerRooms(Number(e.target.value))} className="w-full h-9 bg-zinc-900 border border-zinc-700 rounded-sm px-2 text-[13px] text-zinc-100" /></label>
              </div>
              <button data-testid="concierge-next" onClick={() => setStep(2)} className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-bold text-[12px] uppercase tracking-[0.14em] bg-teal-500 text-zinc-900 hover:bg-teal-400">
                Next — select trades <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-[13px] text-zinc-400">Which trades are in scope?</p>
              <div className="grid grid-cols-2 gap-2">
                {TRADES.map((t) => (
                  <button key={t.key} data-testid={`concierge-trade-${t.key}`} onClick={() => toggle(t.key)}
                    className="rounded-lg px-3 py-2.5 text-[13px] font-semibold text-left transition"
                    style={{ background: selected.has(t.key) ? 'rgba(37,181,168,0.14)' : 'rgba(24,24,27,0.6)', border: `1px solid ${selected.has(t.key) ? '#25B5A8' : 'rgba(63,63,70,0.6)'}`, color: selected.has(t.key) ? '#5eead4' : '#a1a1aa' }}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2.5 rounded-lg text-[12px] text-zinc-400 border border-zinc-700">Back</button>
                <button data-testid="concierge-generate" onClick={generate} disabled={selected.size === 0}
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-bold text-[12px] uppercase tracking-[0.14em] bg-teal-500 text-zinc-900 hover:bg-teal-400 disabled:opacity-40">
                  <Sparkles className="h-4 w-4" /> Generate Startup Package
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center">
              {busy && !pkg && <div className="py-10 flex flex-col items-center gap-3 text-teal-300"><Loader2 className="h-8 w-8 animate-spin" /><span className="text-[12px] uppercase tracking-widest">R² is managing your project…</span></div>}
              {pkg && (
                <>
                  <img src={pkg.firstPageDataUrl} alt="Startup package preview" data-testid="concierge-preview" className="w-full max-w-lg rounded-lg" style={{ border: '1px solid rgba(37,181,168,0.3)' }} />
                  <button data-testid="concierge-download" onClick={download} className="mt-5 w-full max-w-lg flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-bold text-[13px] uppercase tracking-[0.12em] bg-teal-500 text-zinc-900 hover:bg-teal-400">
                    <Download className="h-4 w-4" /> Download Project Startup Package
                  </button>
                  <div className="mt-4 w-full max-w-lg rounded-lg p-4 text-center" style={{ background: 'rgba(252,211,77,0.06)', border: '1px solid rgba(252,211,77,0.28)' }}>
                    <div className="text-[13px] font-bold text-amber-200">This project is now 20% managed by R².</div>
                    <div className="text-[12px] text-zinc-400 mt-1">Start your 100-day trial to unlock full Auto-Pilot control.</div>
                  </div>
                </>
              )}
              {payMsg && <p className="text-[11px] text-zinc-500 mt-3" data-testid="concierge-paymsg">{payMsg}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
